import './RichTextInputToolbar.css';
import React from 'react';
import { css, IButtonProps, IconButton, IContextualMenuItem, Stack } from 'office-ui-fabric-react';
import { EditorState } from 'draft-js';
import { colors } from '../../PPMXColorPicker/PPMXColorPicker';
import { LinkEditor } from './LinkEditor';
import { RichtextEditorCommand, RichtextEditorCommands } from './commands';
import { notUndefined } from '../../../utils/common';
import { colorStyleMap, highlightStyleMap, isMacOS } from './common';
import { EditorUtils } from './EditorUtils';

type RichTextInputToolbarProps = {
    editorState: EditorState,
    onCommand: (newEditorState?: EditorState) => void,
}

export interface IRichTextInputToolbar {
    openLinkEditor: () => void;
}

export const RichtextToolbarButtonClassName = 'richtext-toolbar-button';

export const RichTextInputToolbar = React.forwardRef<IRichTextInputToolbar, RichTextInputToolbarProps>((props, ref) => {
    const [showLinkEditor, setShowLinkEditor] = React.useState(false);
    const [urlValue, setUrlValue] = React.useState<string | undefined>();
    const [stylesBeforePopup, setStylesBeforePopup] = React.useState<string[]>([]);

    const openLinkEditor = React.useCallback((editorState: EditorState) => {
        const selection = editorState.getSelection();
        if (!selection.isCollapsed()) {
            setUrlValue(EditorUtils.getLinkUrl(editorState));
            setShowLinkEditor(true);
        }
    }, [props.editorState, showLinkEditor, urlValue]);

    React.useImperativeHandle(ref, (): IRichTextInputToolbar => ({ openLinkEditor: () => openLinkEditor(props.editorState) }));

    const getToolbarCommands = (editorState: EditorState): IContextualMenuItem[] => {
        const currentStyle = editorState.getCurrentInlineStyle();
        const selection = editorState.getSelection();
        const blockType = editorState
            .getCurrentContent()
            .getBlockForKey(selection.getStartKey())
            .getType();

        const currentTextColor = EditorUtils.hasStyleFromMap(currentStyle, colorStyleMap)?.color;
        const currentHighlightColor = EditorUtils.hasStyleFromMap(currentStyle, highlightStyleMap)?.backgroundColor;

        const fontSizeItems = [
            RichtextEditorCommands["unstyled"],
            RichtextEditorCommands["header-one"],
            RichtextEditorCommands["header-two"],
            RichtextEditorCommands["header-three"],
            RichtextEditorCommands["header-four"]];

        return [
            {
                key: "btnFontStyle",
                iconProps: { iconName: fontSizeItems.find(_ => _.style === blockType)?.iconName ?? "FontSize" },
                title: "Font size",
                menuProps: {
                    className: RichtextToolbarButtonClassName,
                    items: fontSizeItems
                        .map(_ => ({
                            ...buildMenuItem(_),
                            className: css(RichtextToolbarButtonClassName, _.style === blockType ? " checked" : ""),
                            iconProps: { iconName: _.style === blockType ? "CheckMark" : _.iconName },
                            onClick: () => props.onCommand(_.onCommand(editorState))
                        })),
                }
            },
            { key: "separator1" },
            ...[RichtextEditorCommands.bold, RichtextEditorCommands.italic, RichtextEditorCommands.underline, RichtextEditorCommands.strikethrough]
                .map(_ => ({
                    ...buildMenuItem(_),
                    checked: currentStyle.has(_.style!) || stylesBeforePopup.includes(_.style!),
                    onMouseDown: (e: any) => {
                        e.preventDefault();
                        props.onCommand(_.onCommand(editorState));
                    }
                })),
            { key: "separator2" },
            {
                key: "btnFontColor",
                iconProps: {
                    iconName: RichtextEditorCommands.color.iconName,
                    style: { color: currentTextColor },
                },
                title: RichtextEditorCommands.color.name,
                className: RichtextToolbarButtonClassName,
                onMouseDown: () => {
                    if (editorState.getSelection().isCollapsed()) {
                        const stylesRequiresToRemeber = editorState.getCurrentInlineStyle().toArray().filter(_ => !_.startsWith("color"));
                        setStylesBeforePopup(stylesRequiresToRemeber);
                    }
                },
                menuProps: {
                    className: RichtextToolbarButtonClassName,
                    items: [
                        ...[...Array(24).keys()]
                            .map<IContextualMenuItem>(i => {
                                const colorCode = `color${i}`;
                                return {
                                    key: colorCode,
                                    className: RichtextToolbarButtonClassName,
                                    onRender: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void) =>
                                        <ColorStyleButton
                                            color={colors[i].color}
                                            checked={currentStyle.has(colorCode)}
                                            dismissMenu={dismissMenu}
                                            onCommand={() => {
                                                props.onCommand(RichtextEditorCommands.color.onCommand(editorState, { toggledStyle: colorCode, stylesState: stylesBeforePopup }));
                                            }}
                                        />
                                };
                            }),
                        {
                            key: "reset-color",
                            onRender: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void) =>
                                <ColorStyleButton
                                    color='transparent'
                                    dismissMenu={dismissMenu}
                                    onCommand={() => {
                                        props.onCommand(RichtextEditorCommands.color.onCommand(editorState, { toggledStyle: "transparent", stylesState: stylesBeforePopup }));
                                    }}
                                />
                        }
                    ]
                }
            },
            {
                key: "btnHighlight",
                iconProps: {
                    iconName: RichtextEditorCommands.highlight.iconName,
                    style: { color: currentHighlightColor },
                },
                title: RichtextEditorCommands.highlight.name,
                className: RichtextToolbarButtonClassName,
                onMouseDown: () => {
                    if (editorState.getSelection().isCollapsed()) {
                        const stylesRequiresToRemeber = editorState.getCurrentInlineStyle().toArray().filter(_ => !_.startsWith("highlight"));
                        setStylesBeforePopup(stylesRequiresToRemeber);
                    }
                },
                menuProps: {
                    className: RichtextToolbarButtonClassName,
                    items: [
                        ...[...Array(24).keys()]
                            .map<IContextualMenuItem>(i => {
                                const highlightCode = `highlight${i}`;
                                return {
                                    key: highlightCode,
                                    className: RichtextToolbarButtonClassName,
                                    onRender: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void) =>
                                        <ColorStyleButton
                                            color={colors[i].color}
                                            checked={currentStyle.has(highlightCode)}
                                            dismissMenu={dismissMenu}
                                            onCommand={() => {
                                                props.onCommand(RichtextEditorCommands.highlight.onCommand(editorState, { toggledStyle: highlightCode, stylesState: stylesBeforePopup }));
                                            }}
                                        />
                                };
                            }),
                        {
                            key: "reset-color",
                            className: RichtextToolbarButtonClassName,
                            onRender: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void) =>
                                <ColorStyleButton
                                    color='transparent'
                                    dismissMenu={dismissMenu}
                                    onCommand={() => {
                                        props.onCommand(RichtextEditorCommands.highlight.onCommand(editorState, { toggledStyle: "transparent", stylesState: stylesBeforePopup }));
                                    }}
                                />
                        }
                    ]
                }
            },
            { key: "separator3" },
            {
                ...buildMenuItem(RichtextEditorCommands.ul),
                checked: RichtextEditorCommands.ul.style === blockType,
                onClick: () => props.onCommand(RichtextEditorCommands.ul.onCommand(editorState))
            },
            {
                ...buildMenuItem(RichtextEditorCommands.ol),
                checked: RichtextEditorCommands.ol.style === blockType,
                onClick: () => props.onCommand(RichtextEditorCommands.ol.onCommand(editorState))
            },
            { key: "separator4" },
            {
                ...buildMenuItem(RichtextEditorCommands.link),
                onClick: () => openLinkEditor(editorState),
            },
            { key: "separator5" },
            {
                ...buildMenuItem(RichtextEditorCommands.clear),
                onClick: () => props.onCommand(RichtextEditorCommands.clear.onCommand(editorState))
            },
        ];
    }

    const commands = getToolbarCommands(props.editorState);

    return <>
        {showLinkEditor
            && <div className='toolbar'>
                <LinkEditor
                    value={urlValue}
                    onClose={() => {
                        setShowLinkEditor(false);
                        props.onCommand();
                    }}
                    onComplete={(url: string) => props.onCommand(RichtextEditorCommands.link.onCommand(props.editorState, url))}
                /></div>}
        {!showLinkEditor && !!commands?.length
            && <Stack className='toolbar' horizontal>
                {commands.map(_ => _.key.startsWith("separator")
                    ? <div key={_.key} className="separator" />
                    : <IconButton key={_.key}
                        style={{ height: 24 }}
                        {..._ as IButtonProps}
                        onClick={(ev) => { ev.stopPropagation(); _.onClick?.(); }}
                        onAfterMenuDismiss={() => setStylesBeforePopup([])}
                    />
                )}
            </Stack>}
    </>;
})

const buildMenuItem = (cmd: RichtextEditorCommand): IContextualMenuItem => {
    let title: string | undefined = undefined;
    if (cmd.name) {
        const hotKeyText = cmd.hotKey
            ? (isMacOS()
                ? [cmd.hotKey.macos.cmd ? "Cmd" : undefined, cmd.hotKey.macos.option ? "Option" : undefined, cmd.hotKey.key.toUpperCase()]
                : [cmd.hotKey.win.ctrl ? "Ctrl" : undefined, cmd.hotKey.win.alt ? "Alt" : undefined, cmd.hotKey.key.toUpperCase()])
                .filter(notUndefined)
                .join("+")
            : undefined;

        title = hotKeyText
            ? `${cmd.name} (${hotKeyText})`
            : cmd.name;
    }

    return {
        key: `btn${cmd.style}`,
        text: cmd.name,
        iconProps: { iconName: cmd.iconName },
        className: RichtextToolbarButtonClassName,
        title,
    };
}

const ColorStyleButton = (props: {
    color?: string,
    checked?: boolean,
    dismissMenu: (ev?: any, dismissAll?: boolean) => void,
    onCommand?: () => void
}) => {
    const isTransparent = props.color === "transparent";
    return <span
        className={css(RichtextToolbarButtonClassName, isTransparent ? 'reset-color-button' : 'color-button')}
        aria-hidden={true}
        style={isTransparent ? undefined : { backgroundColor: props.color }}
        onMouseDown={e => {
            props.dismissMenu(e, true);
            props.onCommand?.();
        }}
    >{isTransparent ? 'Remove color' : props.checked ? '\u2714' : ''}</span>;
}
